/**
 * Text normalization for moderation matching.
 *
 * Everything here exists to defeat obfuscation. An attacker registering
 * `pаypal-verify` (Cyrillic а), `p4yp4l`, or `p-a-y-p-a-l` is trying to look
 * like PayPal to a human while looking like nothing to a naive string match.
 * The fix is to collapse all of those into one canonical form -- a "skeleton"
 * -- and match on that.
 *
 * The skeleton is applied to BOTH the input and the blocklist, so the two meet
 * in the middle. It is deliberately lossy and must never be shown to a user or
 * stored as their content.
 */

/**
 * Confusable characters, grouped by the ASCII letter they resolve to.
 *
 * Scope note: NFKD plus combining-mark stripping already folds accented Latin
 * (é -> e) and NFKC folds fullwidth and mathematical alphanumerics, so this
 * table only needs the cross-script lookalikes and digit/symbol substitutions
 * that Unicode normalization leaves alone.
 */
const CONFUSABLE_GROUPS: Readonly<Record<string, string>> = {
  a: 'a4@аαåæ',           // Cyrillic a, Greek alpha
  b: 'b8вβß',                   // Cyrillic ve, Greek beta
  c: 'cсς¢',                    // Cyrillic es
  d: 'dԁɗ',
  e: 'e3еε€',                   // Cyrillic ie, Greek epsilon
  f: 'fƒ',
  g: 'g9qɡ',
  h: 'hнһ',                          // Cyrillic en (looks like H)
  i: 'il1|!іıłιӏ',    // i/l/1 collapse into one class
  j: 'jј',
  k: 'kкκ',
  m: 'mм',
  n: 'nո',
  o: 'o0оοøσօ',       // Cyrillic o, Greek omicron
  p: 'pрρ',                          // Cyrillic er, Greek rho
  r: 'rг',
  s: 's5$ѕ',                              // Cyrillic dze
  t: 't7+тτ',
  u: 'uυц',
  v: 'vνѵ',                          // Greek nu
  w: 'wшᴡ',
  x: 'xхχ',                          // Cyrillic ha, Greek chi
  y: 'yуγ',                          // Cyrillic u
  z: 'z2ʐ',
}

/** Reverse index: every confusable character -> its canonical ASCII letter. */
const SKELETON_MAP: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>()
  for (const [canonical, variants] of Object.entries(CONFUSABLE_GROUPS)) {
    for (const char of variants) map.set(char, canonical)
  }
  return map
})()

/** Characters treated as word separators rather than content. */
const SEPARATORS = /[\s\-_.,:;/\\|()[\]{}'"`~*=]+/g

/**
 * Fold text to its confusable skeleton.
 *
 * Note the digit collapse is total: `1` becomes `i`, `0` becomes `o`, `5`
 * becomes `s`. That means a legitimately numeric name normalizes oddly
 * ("1024" -> "iozy"), which is harmless -- it only ever needs to not collide
 * with a brand, and it never round-trips back to the user.
 */
export function skeleton(input: string): string {
  const decomposed = input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining marks: é -> e
    .toLowerCase()

  let out = ''
  for (const char of decomposed) {
    out += SKELETON_MAP.get(char) ?? char
  }
  return out
}

/** Skeleton with separators preserved as single spaces -- keeps token boundaries. */
export function tokenSkeleton(input: string): string {
  return skeleton(input).replace(SEPARATORS, ' ').trim()
}

/**
 * Skeleton with every separator removed.
 *
 * This is what catches `p-a-y-p-a-l` and `c o i n b a s e`; matching only on
 * tokens would miss both.
 */
export function squashedSkeleton(input: string): string {
  return skeleton(input).replace(SEPARATORS, '').replace(/[^a-z0-9]/g, '')
}

/** Split into skeleton tokens, dropping empties. */
export function tokens(input: string): readonly string[] {
  return tokenSkeleton(input).split(' ').filter((t) => t.length > 0)
}
